import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { FaArrowLeft, FaPlusSquare } from 'react-icons/fa'
import { useDispatch, useSelector } from 'react-redux'
import { fetchPaymentMethods , buyProperty  } from '../../store/reducers/paymentReducer'
import type { RootState, AppDispatch } from '../../store/store'

import './OrderReview.css'

interface LocationState {
  buildingName:  string
  price:         number
  salesTax:      number
  processingFee: number
  total:         number
  propertyId:    string
  lat:           number
  lng:           number
}

export default function OrderReview() {
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const { state } = useLocation()
  const data = state as LocationState

  const [showConfirm, setShowConfirm] = useState(false)

  useEffect(() => {
    if (!data || !data.buildingName) {
      navigate(-1)
    }
  }, [data, navigate])

  useEffect(() => {
    dispatch(fetchPaymentMethods())
  }, [dispatch])

  const {
    cardList,
    isFetching,
    fetchError,
    isBuying,
    buyError,
    buySuccessMsg
  } = useSelector((s: RootState) => s.payment)

  const defaultCard = cardList.find(c => c.isDefault)

  useEffect(() => {
    if (buySuccessMsg) {
      alert(buySuccessMsg)
      navigate(-1)
    }
  }, [buySuccessMsg, navigate])

  useEffect(() => {
    if (buyError) {
      alert(`Purchase failed: ${buyError}`)
    }
  }, [buyError])

  if (!data || !data.buildingName) return null

  const handleContinue = () => {
    if (!defaultCard) {
      alert('Please add a card first.')
      return
    }
    setShowConfirm(true)
  }

  const handleBuy = () => {
    setShowConfirm(false)
    dispatch(buyProperty({
      propertyId: data.propertyId,
      paymentMethodId: defaultCard!.id,
      bidAmount: data.price,
      name: data.buildingName,
      lat: data.lat,
      lng: data.lng
    }))
  }

  return (
    <>
      <div className="order-container">
        <div className="order-header">
          <FaArrowLeft
            className="order-back-icon"
            onClick={() => navigate(-1)}
          />
          <h2 className="order-screen-title">Order Review</h2>
          <button
            className="header-add-card-btn"
            onClick={() => navigate('/Add-Card')}
          >
            <FaPlusSquare className="add-icon" />
            Add Card
          </button>
        </div>

        <h1 className="order-title">{data.buildingName}</h1>

        <div className="order-section">
          <div className="order-row">
            <span className="label">Price</span>
            <span className="value">${data.price.toFixed(2)}</span>
          </div>
          <div className="order-row">
            <span className="label">Sales Tax</span>
            <span className="value">${data.salesTax.toFixed(2)}</span>
          </div>
          <div className="order-row">
            <span className="label">Processing Fee</span>
            <span className="value">${data.processingFee.toFixed(2)}</span>
          </div>
        </div>

        <div className="divider" />

        <div className="order-section total-section">
          <p className="order-note">
            *All applicable taxes are included in price.
          </p>
          <div className="order-row">
            <strong className="label">Total (incl. taxes)*</strong>
            <strong className="value">${data.total.toFixed(2)}</strong>
          </div>
        </div>

        <div className="divider" />

        <div className="order-section">
          {isFetching ? (
            <div className="order-row">
              <span className="label">Payment Method</span>
              <span className="value">Loading…</span>
            </div>
          ) : fetchError ? (
            <div className="order-row">
              <span className="label">Payment Method</span>
              <span className="value error">{fetchError}</span>
            </div>
          ) : defaultCard ? (
            <div className="order-row">
              <span className="label">Payment Method</span>
              <span className="value">
                {defaultCard.type} •••• {defaultCard.last4}
              </span>
            </div>
          ) : (
            <div className="order-row">
              <span className="label">Payment Method</span>
              <span className="value">No card on file</span>
            </div>
          )}
        </div>

        <div className="divider" />

        <button
          className="continue-btn"
          onClick={handleContinue}
          disabled={isBuying}
        >
          {isBuying ? 'Processing…' : 'Continue to Pay'}
        </button>
      </div>

      {showConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Confirm Purchase</h3>
            <p>
              Are you sure you want to buy{' '}
              <strong>{data.buildingName}</strong> for{' '}
              <strong>${data.total.toFixed(2)}</strong>?
            </p>
            <div className="modal-actions">
              <button
                className="modal-btn cancel"
                onClick={() => setShowConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="modal-btn buy"
                onClick={handleBuy}
                disabled={isBuying}
              >
                {isBuying ? 'Buying…' : 'Buy'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
